import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

export const roleApi = createApi({
    reducerPath: 'roleApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Role', 'Permission'],
    endpoints: (builder) => ({
        getRoles: builder.query({
            query: () => '/roles',
            transformResponse: (response) => {
                if (response.success && response.data) {
                    return response.data.rows || response.data;
                }
                return [];
            },
            providesTags: ['Role'],
        }),
        createRole: builder.mutation({
            query: (roleData) => ({
                url: '/roles',
                method: 'POST',
                body: roleData,
            }),
            invalidatesTags: ['Role'],
        }),
        updateRole: builder.mutation({
            query: ({ id, ...roleData }) => ({
                url: `/roles/${id}`,
                method: 'PUT',
                body: roleData,
            }),
            invalidatesTags: ['Role'],
        }),
        deleteRole: builder.mutation({
            query: (id) => ({
                url: `/roles/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Role'],
        }),

        // Permission Management
        getAllPermissions: builder.query({
            query: () => '/permissions?limit=1000',
            transformResponse: (response) => {
                // Return list of permission definitions
                return response.success ? (response.data.rows || response.data) : [];
            },
            providesTags: ['Permission']
        }),

        getRolePermissions: builder.query({
            query: (roleId) => `/permissions/role/${roleId}`,
            transformResponse: (response) => {
                // Return list of assignments
                return response.success ? response.data : [];
            },
            providesTags: (result, error, roleId) => [{ type: 'Permission', id: roleId }],
        }),

        updateRolePermissions: builder.mutation({
            query: ({ roleId, permissions }) => {
                // Transform flat UI permissions back to API format
                // Transform flat UI permissions back to API format and sanitize
                const uniquePermissions = new Map();
                permissions.forEach(p => {
                    if (p.id) {
                        uniquePermissions.set(p.id, {
                            permissionId: p.id,
                            canView: Boolean(p.view),
                            canCreate: Boolean(p.create),
                            canEdit: Boolean(p.edit),
                            canDelete: Boolean(p.delete),
                            canApprove: Boolean(p.approve),
                            specialPermission: p.specialPermissions || null
                        });
                    }
                });

                const permissionsData = Array.from(uniquePermissions.values());

                return {
                    url: `/permissions/role/${roleId}/assign`,
                    method: 'POST',
                    body: { permissionsData },
                };
            },
            invalidatesTags: (result, error, { roleId }) => [{ type: 'Permission', id: roleId }],
        }),
    }),
});

export const {
    useGetRolesQuery,
    useCreateRoleMutation,
    useUpdateRoleMutation,
    useDeleteRoleMutation,
    useGetRolePermissionsQuery,
    useUpdateRolePermissionsMutation,
    useGetAllPermissionsQuery
} = roleApi;
